// CompoundStep — the single step renderer for every wizard stage.
// Replaces the per-kind switch that used to live in App.tsx:310–338.
//
// Pipeline per render:
//   1. Walk stage.blocks (the wizardBlockRef[] from config).
//   2. For each ref, resolve the library entry from
//      config.libraryEntries[] (bundled with the public response).
//   3. Resolve the archetype via @bcbb/wizard-blocks ARCHETYPE_MAP.
//   4. Merge libraryEntry.config with the ref's overrides.
//   5. Evaluate __visibility against draft.payload; skip when falsy.
//   6. Render the archetype. Form archetypes participate in the
//      shared `useStageForm` registry; control archetypes own their
//      own advance() and ignore the registry.
//
// On submit, collect() over the registry produces either an errors map
// (fail fast, show inline) or a patch object (advance with patch).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
// Imported from '@stripe/stripe-js/pure', NOT '@stripe/stripe-js'. The default
// entry point runs `Promise.resolve().then(getStripePromise)` at module scope,
// so merely IMPORTING it injects the js.stripe.com <script> — 257 KB fetched on
// every boot whether or not a payment surface is ever opened. The '/pure' entry
// is the same loadStripe with that top-level call removed, so the script is
// injected only when loadStripe() is actually invoked (which is what the
// memoized helpers below already gate on). Type-only imports stay on the main
// entry: `import type` is erased and pulls in no runtime module.
import { loadStripe } from '@stripe/stripe-js/pure';
import type { Stripe } from '@stripe/stripe-js';
import {
  ARCHETYPE_MAP,
  createStageFormRegistry,
  isVisible,
  resolveBlock,
  type BlockAdapters,
  type StageFormRegistry,
} from '@bcbb/wizard-blocks';
import type { StepProps } from '../App';
import { PrimaryButton, GhostButton } from '../components/ui';
import {
  fetchPlans,
  loadStoredCredentials,
  resolveLocationKey,
  completeDraft as apiCompleteDraft,
  createSetupIntent,
  sendVerification as apiSendVerification,
  confirmVerification as apiConfirmVerification,
  type WizardBlockRef,
} from '../lib/draftClient';

// ── Stripe setup-intent form ──────────────────────────────────────────────
// Self-contained inline component that mounts inside the StripePayment archetype
// slot. Fetches a SetupIntent from the signup-draft endpoint, renders Stripe
// Elements, and calls back onSuccess(pmId) once confirmed.

const stripePromiseCache = new Map<string, Promise<Stripe | null>>();
function getStripePromise(publishableKey: string) {
  let p = stripePromiseCache.get(publishableKey);
  if (!p) {
    p = loadStripe(publishableKey);
    stripePromiseCache.set(publishableKey, p);
  }
  return p;
}

interface SignupStripeFormProps {
  draftId: string;
  token: string;
  onSuccess: (pmId: string) => void;
  onError: (msg: string) => void;
}

function SignupStripeForm({ draftId, token, onSuccess, onError }: SignupStripeFormProps) {
  const [setup, setSetup] = useState<{
    clientSecret: string;
    publishableKey: string;
  } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    createSetupIntent(draftId, token)
      .then((data) => setSetup(data))
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Could not initialise payment form.';
        setLoadError(msg);
        onError(msg);
      });
  }, [draftId, token, onError]);

  if (loadError) {
    return (
      <div className="rounded-md bg-[var(--bcbb-danger-bg,#fde0df)] px-3 py-2 text-sm text-[var(--bcbb-danger)]">
        {loadError}
      </div>
    );
  }

  if (!setup) {
    return (
      <div className="py-4 text-sm text-[var(--bcbb-text-muted)]">
        Setting up secure payment form…
      </div>
    );
  }

  return (
    <Elements
      stripe={getStripePromise(setup.publishableKey)}
      options={{ clientSecret: setup.clientSecret }}
    >
      <SignupStripeConfirmForm onSuccess={onSuccess} onError={onError} />
    </Elements>
  );
}

function SignupStripeConfirmForm({
  onSuccess,
  onError,
}: {
  onSuccess: (pmId: string) => void;
  onError: (msg: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [elementReady, setElementReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements || submitting) return;
    setSubmitting(true);
    setLocalError(null);

    const result = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: `${window.location.origin}/signup` },
      redirect: 'if_required',
    });

    if (result.error) {
      const msg = result.error.message ?? 'Could not save the payment method.';
      setLocalError(msg);
      onError(msg);
      setSubmitting(false);
      return;
    }

    const status = result.setupIntent?.status;
    if (status === 'succeeded') {
      const pmId = result.setupIntent.payment_method;
      if (typeof pmId === 'string' && pmId) {
        onSuccess(pmId);
      } else {
        const msg = 'Payment method confirmed but no ID returned.';
        setLocalError(msg);
        onError(msg);
      }
      setSubmitting(false);
      return;
    }
    const msg = `Unexpected payment status: ${status ?? 'unknown'}. Please try again.`;
    setLocalError(msg);
    onError(msg);
    setSubmitting(false);
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      {!elementReady && (
        <div className="py-4 text-sm text-[var(--bcbb-text-muted)]">Loading payment form…</div>
      )}
      <div className={elementReady ? undefined : 'sr-only'}>
        <PaymentElement onReady={() => setElementReady(true)} />
      </div>
      {localError && (
        <p className="rounded-md bg-[var(--bcbb-danger-bg,#fde0df)] px-3 py-2 text-sm text-[var(--bcbb-danger)]">
          {localError}
        </p>
      )}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={!stripe || !elements || submitting || !elementReady}
          className="rounded-md bg-gradient-to-br from-[var(--bcbb-primary)] to-[var(--bcbb-accent)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          {submitting ? 'Saving…' : 'Save payment method'}
        </button>
      </div>
    </form>
  );
}

declare global {
  // Mapbox Search JS — injected by index.signup.html.
  interface Window {
    mapboxsearch?: {
      autofill: (opts: { options: { country: string } }) => void;
      config: { accessToken: string };
    };
  }
}

interface CompoundStepProps extends StepProps {
  stage: { id: string; kind: string; title?: string; blocks: WizardBlockRef[] };
}

export function CompoundStep({
  stage,
  draft,
  config,
  advance,
  back,
  isAdvancing,
}: CompoundStepProps) {
  const registryRef = useRef<StageFormRegistry>(createStageFormRegistry());
  const [submitErrors, setSubmitErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Adapter wiring — portal-specific implementations of the package's
  // host-provided hooks. Stable per render via useMemo so child blocks
  // don't see a new object every commit.
  const adapters = useMemo<BlockAdapters>(
    () => ({
      enableMapboxAutofill: (_form: HTMLFormElement) => {
        let cancelled = false;
        const enable = () => {
          if (cancelled || !window.mapboxsearch) return;
          try {
            window.mapboxsearch.autofill({ options: { country: 'us' } });
          } catch {
            /* mapboxsearch may not have loaded yet — retry */
          }
        };
        enable();
        const t = window.setInterval(enable, 250);
        const stop = window.setTimeout(() => window.clearInterval(t), 4000);
        return () => {
          cancelled = true;
          window.clearInterval(t);
          window.clearTimeout(stop);
        };
      },
      resolveLocationKey: async (input) => {
        try {
          const res = await resolveLocationKey({
            draftId: draft.draftId,
            line1: input.line1,
            city: input.city,
            state: input.state,
            postalCode: input.postalCode,
          });
          return res.locationKey;
        } catch {
          // Resolver bearer not yet wired in dev — fall through.
          return null;
        }
      },
      fetchPlans: async (locationKey) => {
        const res = await fetchPlans(locationKey);
        return res.items;
      },
      completeDraft: async () => {
        const stored = loadStoredCredentials();
        if (!stored) throw new Error('Session lost. Please reload to start over.');
        const res = await apiCompleteDraft(draft.draftId, stored.token);
        // Stash the redirect URL on the payload so the Completed
        // archetype can read it via its configured redirectKey.
        return { redirectUrl: res.redirectUrl };
      },
      renderStripePaymentForm: ({ onSuccess, onError }) => {
        const stored = loadStoredCredentials();
        if (!stored) {
          const msg = 'Session lost. Please reload to start over.';
          onError(msg);
          return null;
        }
        return (
          <SignupStripeForm
            draftId={draft.draftId}
            token={stored.token}
            onSuccess={onSuccess}
            onError={onError}
          />
        );
      },
      sendVerification: async (channel) => {
        const stored = loadStoredCredentials();
        if (!stored) throw new Error('Session lost. Please reload to start over.');
        await apiSendVerification(draft.draftId, stored.token, channel);
      },
      confirmVerification: async (channel, verifyToken) => {
        const stored = loadStoredCredentials();
        if (!stored) throw new Error('Session lost. Please reload to start over.');
        await apiConfirmVerification(draft.draftId, stored.token, channel, verifyToken);
      },
      quizPool: config.quiz,
      upsellRules: config.upsells,
    }),
    [draft.draftId, config.quiz, config.upsells],
  );

  // Resolve blocks once per render. Anything that fails to resolve
  // produces a console.warn and is skipped — the SPA degrades to "as
  // if the block weren't there" rather than crashing.
  const resolved = useMemo(() => {
    const out: Array<{
      blockId: string;
      ref: WizardBlockRef;
      Component: React.ComponentType<unknown>;
      mergedConfig: Record<string, unknown>;
      isFormArchetype: boolean;
    }> = [];
    for (const ref of stage.blocks) {
      const result = resolveBlock(ref, config.libraryEntries, ARCHETYPE_MAP);
      if ('reason' in result) {
        // eslint-disable-next-line no-console
        console.warn('[CompoundStep]', result.reason, result.detail);
        continue;
      }
      out.push({
        blockId: ref.blockId,
        ref,
        Component: result.archetype.component as React.ComponentType<unknown>,
        mergedConfig: result.config,
        isFormArchetype: Boolean(result.archetype.isFormArchetype),
      });
    }
    return out;
  }, [stage.blocks, config.libraryEntries]);

  const hasFormBlocks = resolved.some((r) => r.isFormArchetype);

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (submitting) return;
      setSubmitting(true);
      setSubmitErrors({});
      const collected = registryRef.current.collect();
      if ('errors' in collected) {
        setSubmitErrors(collected.errors);
        setSubmitting(false);
        return;
      }
      try {
        await advance(collected.patch);
      } finally {
        setSubmitting(false);
      }
    },
    [advance, submitting],
  );

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4">
      {resolved.map(({ blockId, ref, Component, mergedConfig, isFormArchetype }) => {
        if (!isVisible(ref.__visibility, draft.payload as Record<string, unknown>)) return null;
        const props = {
          blockId,
          config: mergedConfig,
          payload: draft.payload as Record<string, unknown>,
          registry: isFormArchetype ? registryRef.current : null,
          adapters,
          advance,
          back,
          isAdvancing,
        };
        return <Component key={blockId} {...props} />;
      })}

      {Object.keys(submitErrors).length > 0 && (
        <div className="rounded-md bg-[var(--bcbb-danger-bg,#fde0df)] px-3 py-2 text-sm text-[var(--bcbb-danger)]">
          Please fix the highlighted fields and try again.
        </div>
      )}

      {/* Only render the Continue button when at least one form
          archetype is on the stage. Control archetypes (PlanPicker,
          Quiz, UpsellOffer, Review, Completed) own their own advance
          flows and a footer button here would be redundant + confusing. */}
      {hasFormBlocks && (
        <div className="mt-6 flex items-center justify-between gap-3">
          <GhostButton type="button" onClick={back} disabled={submitting || isAdvancing}>
            Back
          </GhostButton>
          <PrimaryButton type="submit" disabled={submitting || isAdvancing}>
            {submitting || isAdvancing ? 'Saving…' : 'Continue'}
          </PrimaryButton>
        </div>
      )}
    </form>
  );
}
